// Library methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme, Theme } from "@mui/material/styles";

// Styles
import {
  getSurfaceTokens,
  getRequiredErrorColor,
} from "src/styles/mui/muiStylesHelper";

const getCardBackground = (theme: Theme) =>
  theme.palette.mode === "dark" ? "#00000090" : "rgba(255, 255, 255, 0.55)";

type EMRJobType = {
  jobId: string;
  title: string;
  status: string;
  fileCount: number;
  createdAt: string;
  completedAt: string | null;
};

type JobCardType = {
  job: EMRJobType;
};

const JobCard = ({ job }: JobCardType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  const statusColor = useMemo(() => {
    if (job?.status === "Failed") {
      return getRequiredErrorColor(theme);
    } else if (job?.status === "Complete") {
      return "#22BE39";
    }

    return "#E2772E";
  }, [job?.status, theme]);

  return (
    <Box
      display="flex"
      alignItems="center"
      gap="16px"
      sx={{
        border:
          theme.palette.mode === "dark"
            ? "1px solid #5B6D7A"
            : "1px solid rgba(0, 0, 0, 0.2)",
        backgroundColor: getCardBackground(theme),
        borderRadius: "8px",
        width: "100%",
        padding: "8px",
      }}
    >
      <Typography
        sx={{
          flexShrink: 0,
          minWidth: "90px",
          textAlign: "center",
          color: statusColor,
          fontSize: "14px",
          fontWeight: 600,
          border: `1px solid ${statusColor}`,
          borderRadius: "4px",
          p: "0px 4px",
        }}
      >
        {t(job.status)}
      </Typography>

      <Typography
        fontSize="14px"
        fontWeight={600}
        color={onSurface}
        sx={{
          flex: 1,
          minWidth: "150px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {job.title}
      </Typography>

      <Typography
        fontSize="14px"
        color={onSurface}
        sx={{ flexShrink: 0, minWidth: "80px" }}
      >
        {job?.fileCount ?? 0}
      </Typography>

      <Typography
        fontSize="14px"
        color={onSurface}
        sx={{ flexShrink: 0, minWidth: "130px" }}
      >
        {job?.createdAt?.slice(0, 10) ?? ""}
      </Typography>

      <Typography
        fontSize="14px"
        color={onSurface}
        sx={{ flexShrink: 0, minWidth: "130px" }}
      >
        {job?.completedAt?.slice(0, 10) ?? t("N/A")}
      </Typography>
    </Box>
  );
};

export default JobCard;
